import { Card, Form, Input, Button, Space, Message, Checkbox } from "@arco-design/web-react";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ItemAPI } from "@/apis/material";
import { InputNumber, InputCurrency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import AccountSelect from "@/components/AccountSelect";
import UOMSelect from "@/components/UOMSelect";
import VendorSelect from "@/components/VendorSelect";
import Decimal from "decimal.js";
import { StatusDef } from "@/enums/status";
import { ItemTypeDef, ItemType, ItemSafetyStockType } from "@/enums/material";
import { AccountType, AccountCategory } from "@/enums/accounting";

const FormItem = Form.Item;
const TextArea = Input.TextArea;

export const ItemEdit = () => {
    const params = useParams();
    const itemId = params.itemId;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uom, setUOM] = useState();
    const [itemType, setItemType] = useState();
    const [cogsAccountLabel, setCogsAccountLabel] = useState();
    const [safetyStockLevelDaysEnabled, setSafetyStockLevelDaysEnabled] = useState();
    const [safetyStockLevelUomEnabled, setSafetyStockLevelUomEnabled] = useState();
    const [safetyQuantityUpdateTime, setSafetyQuantityUpdateTime] = useState();
    const [safetyStockQuantity, setSafetyStockQuantity] = useState();
    const [itemUom, setItemUom] = useState();

    const onUOMChange = (val) => {
        setUOM(val);
    };

    const onItemTypeChange = (value) => {
        setItemType(value);
        setCogsAccountLabel(value === ItemType.NonInventory ? "COGS/Expense Account" : "COGS Account");
    };

    const onSalePriceChange = (val) => {
        const cost = form.getFieldValue("stdCost");
        const margin = calcMargin(cost, val);
        if (margin == null) return;
        form.setFieldsValue({ defaultMargin: margin, defaultMarginPercent: margin * 100 });
    };

    const onMarginChange = (val) => {
        const cost = form.getFieldValue("stdCost");
        if (cost == null || val == null || val === 1) return;
        const price = new Decimal(cost / (1 - val)).toDecimalPlaces(5).toNumber();
        form.setFieldsValue({ salePrice: price });
    };

    const calcMargin = (cost, price) => {
        if (cost == null || price == null || price === 0) return null;
        return new Decimal((price - cost) / price).toDecimalPlaces(5).toNumber();
    };

    const getInfo = useCallback(async () => {
        try {
            const values = await ItemAPI.get(itemId);
            if (values.uom) {
                setUOM(values.uom);
                setItemUom(values.uom);
            }
            onItemTypeChange(values.itemType);
            values.defaultMargin = calcMargin(values.stdCost, values.salePrice);
            if (values.defaultMargin != null) {
                values.defaultMarginPercent = values.defaultMargin * 100;
            }
            if (values.safetyStockLevel == null) {
                values.safetyStockLevel = 60;
                setSafetyStockLevelDaysEnabled(true);
            } else {
                if (values.safetyStockType === ItemSafetyStockType.Days) {
                    setSafetyStockLevelDaysEnabled(true);
                } else if (values.safetyStockType === ItemSafetyStockType.UOM) {
                    setSafetyStockLevelUomEnabled(true);
                }
            }
            if (values.safetyQuantityUpdateTime) {
                setSafetyQuantityUpdateTime(values.safetyQuantityUpdateTime);
                setSafetyStockQuantity(values.safetyStockQuantity);
            }
            form.setFieldsValue(values);
        } catch (error) {
            Message.error(error.message);
        }
    }, [itemId, form]);

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            if (safetyStockLevelDaysEnabled) {
                values.safetyStockType = ItemSafetyStockType.Days;
            } else if (safetyStockLevelUomEnabled) {
                values.safetyStockType = ItemSafetyStockType.UOM;
            } else {
                values.safetyStockType = ItemSafetyStockType.None;
            }
            setLoading(true);
            await ItemAPI.update(itemId, values);
            Message.success("Item updated successfully");
            navigate(`/workspace/material/items/${itemId}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to update item");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card title="Edit Item" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Save</Button>
                <Button onClick={() => navigate(`/workspace/material/items/${itemId}`)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 15 }}
            >
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem field="status" label="Status" required rules={[{ required: true }]}>
                    <EnumSelect enumObj={StatusDef} />
                </FormItem>
                <FormItem field="itemType" label="Item Type" required rules={[{ required: true }]}>
                    <EnumSelect enumObj={ItemTypeDef} onChange={onItemTypeChange} />
                </FormItem>
                <FormItem field="itemNo" label="Item Number" required rules={[{ required: true }]}>
                    <Input />
                </FormItem>
                <FormItem field="itemName" label="Item Name" required rules={[{ required: true }]}>
                    <Input />
                </FormItem>
                <FormItem field="description" label="Description">
                    <TextArea />
                </FormItem>
                <FormItem field="stdCost" label="Standard Cost">
                    <InputCurrency onChange={val => {
                        const margin = form.getFieldValue("defaultMargin");
                        const price = form.getFieldValue("salePrice");
                        if (margin != null) {
                            onMarginChange(margin);
                        } else if (price != null) {
                            onSalePriceChange(price);
                        }
                    }} />
                </FormItem>
                <FormItem field="uom" label="Unit of Measure(UOM)" required rules={[{ required: true }]}>
                    <UOMSelect onChange={onUOMChange} />
                </FormItem>
                <FormItem label="Additional UOM">
                    <Space>
                        <FormItem field="additionalUom" style={{ width: 120 }}>
                            <UOMSelect disabledList={[uom]} />
                        </FormItem>
                        <FormItem>=</FormItem>
                        <FormItem field="additionalUomThreshold">
                            <InputNumber suffix={uom} />
                        </FormItem>
                    </Space>
                </FormItem>
                <FormItem field="defaultMarginPercent" label="Default Margin">
                    <InputNumber precision={3} onChange={val => {
                        const margin = val / 100;
                        form.setFieldValue("defaultMargin", margin);
                        onMarginChange(margin);
                    }} suffix="%" />
                </FormItem>
                <FormItem field="defaultMargin" hidden>
                    <Input />
                </FormItem>
                <FormItem field="salePrice" label="Sales Price">
                    <InputCurrency onChange={val => onSalePriceChange(val)} />
                </FormItem>
                <FormItem field="cogsAccountId" label={cogsAccountLabel} required rules={[{ required: true }]}>
                    {itemType === ItemType.NonInventory ?
                        <AccountSelect category={AccountCategory.Expenses} />
                        :
                        <AccountSelect type={AccountType.COGS} />
                    }
                </FormItem>
                <FormItem field="incomeAccountId" label="Income Account" required rules={[{ required: true }]}>
                    <AccountSelect type={AccountType.Income} />
                </FormItem>
                {itemType === ItemType.Inventory &&
                    <FormItem field="inventoryAccountId" label="Inventory Account" required rules={[{ required: true }]}>
                        <AccountSelect type={AccountType.Inventory} />
                    </FormItem>
                }
                <FormItem label="Safety Stock Level">
                    <Space direction="vertical">
                        <Space>
                            <FormItem>
                                <Checkbox checked={safetyStockLevelUomEnabled}
                                    onChange={(checked) => {
                                        setSafetyStockLevelUomEnabled(checked);
                                        setSafetyStockLevelDaysEnabled(!checked);
                                    }} />
                            </FormItem>
                            <FormItem field="safetyStockLevelUom">
                                <InputNumber suffix={uom} style={{ width: 200 }} />
                            </FormItem>
                        </Space>
                        <Space>
                            <FormItem>
                                <Checkbox checked={safetyStockLevelDaysEnabled}
                                    onChange={(checked) => {
                                        setSafetyStockLevelUomEnabled(!checked);
                                        setSafetyStockLevelDaysEnabled(checked);
                                    }} />
                            </FormItem>
                            <FormItem field="safetyStockLevel">
                                <InputNumber suffix="days" style={{ width: 200 }} />
                            </FormItem>
                            {safetyQuantityUpdateTime &&
                                <FormItem>
                                    <span>{safetyStockQuantity} {itemUom} on updated {safetyQuantityUpdateTime}</span>
                                </FormItem>
                            }
                        </Space>
                    </Space>
                </FormItem>
                <FormItem label="Preferred Vendor" style={{ marginBottom: 0 }}>
                    <Space>
                        <FormItem field="preferredVendorId">
                            <VendorSelect />
                        </FormItem>
                        <FormItem field="preferredVendorLeadTime" labelAlign="left" label="Lead Time(days)">
                            <Input />
                        </FormItem>
                    </Space>
                </FormItem>
            </Form>
        </Card>
    );
};
